import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  UserCredential,
  browserLocalPersistence,
  setPersistence,
  Auth,
  User as FirebaseUser,
  updateProfile,
  getAuth,
  AuthError,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, Timestamp, updateDoc, Firestore } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User, Organizer } from '../types/models';
import { 
  AuthUser, 
  AUTH_ERROR_CODES, 
  AUTH_ERROR_MESSAGES,
  SignInCredentials,
  SignUpCredentials,
  UserType,
  Role
} from '../types/auth';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

interface UserData {
  name: string;
  email: string;
  userType: UserType;
  roles: string[];
  phoneNumber?: string;
  gender?: string;
  address?: string;
  website?: string;
  description?: string;
  businessType?: string;
}

class AuthService {
  private static instance: AuthService;
  public auth: Auth;
  private db: Firestore;
  private googleProvider: GoogleAuthProvider;

  private constructor() {
    this.auth = getAuth();
    this.db = db;
    this.googleProvider = googleProvider;
    this.initializeAuth();
    this.handleRedirectResult();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private async initializeAuth(): Promise<void> {
    try {
      await setPersistence(this.auth, browserLocalPersistence);
    } catch (error) {
      console.error('Error setting persistence:', error);
    }
  }

  private async handleRedirectResult(): Promise<void> {
    try {
      const result = await getRedirectResult(this.auth);
      if (result) {
        await this.handleAuthResult(result);
      }
    } catch (error) {
      console.error('Error handling redirect result:', error);
    }
  }

  private isOnline(): boolean {
    return navigator.onLine;
  }

  private handleAuthError(error: any): AuthError {
    const code = error.code as keyof typeof AUTH_ERROR_MESSAGES;
    return {
      code,
      message: AUTH_ERROR_MESSAGES[code] || 'An unexpected error occurred. Please try again.',
      name: error.name || 'AuthError',
      customData: error.customData || {},
    };
  }

  private async handleAuthResult(result: UserCredential): Promise<AuthUser> {
    try {
      const { user: firebaseUser } = result;
      const userRef = doc(this.db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data() as AuthUser;
        return {
          ...userData,
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL || undefined,
          emailVerified: firebaseUser.emailVerified,
        };
      }

      // If user document doesn't exist, create one using the single source of truth.
      const newUser = this.createNewUserObject(firebaseUser);
      await setDoc(userRef, newUser);
      return newUser;
    } catch (error) {
      console.error('Error handling auth result:', error);
      throw this.handleAuthError(error);
    }
  }

  public async signInWithGoogle(): Promise<AuthUser> {
    if (!this.isOnline()) {
      throw this.handleAuthError({
        code: AUTH_ERROR_CODES.NETWORK_ERROR,
        message: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.NETWORK_ERROR]
      });
    }

    try {
      let result: UserCredential;
      try {
        result = await signInWithPopup(this.auth, this.googleProvider);
      } catch (popupError: any) {
        if (popupError.code === AUTH_ERROR_CODES.POPUP_BLOCKED || 
            popupError.code === AUTH_ERROR_CODES.POPUP_CLOSED) {
          await signInWithRedirect(this.auth, this.googleProvider);
          throw this.handleAuthError({
            code: 'auth/redirect-started',
            message: 'Redirecting to Google sign-in...'
          });
        }
        throw popupError;
      }

      return await this.handleAuthResult(result);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public async signInWithEmail(credentials: SignInCredentials): Promise<AuthUser> {
    if (!this.isOnline()) {
      throw this.handleAuthError({
        code: AUTH_ERROR_CODES.NETWORK_ERROR,
        message: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.NETWORK_ERROR]
      });
    }

    try {
      const result = await signInWithEmailAndPassword(
        this.auth,
        credentials.email,
        credentials.password
      );
      return await this.handleAuthResult(result);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public async signUpWithEmail(
    email: string,
    password: string,
    name: string,
    userType: UserType,
    additionalData?: Record<string, any>
  ): Promise<AuthUser> {
    if (!this.isOnline()) {
      throw this.handleAuthError({
        code: AUTH_ERROR_CODES.NETWORK_ERROR,
        message: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.NETWORK_ERROR]
      });
    }

    try {
      // Only allow 'user' or 'organizer' userType
      let safeUserType: UserType = 'user';
      let safeRoles: Role[] = ['user'];
      let userDocData: any = {};
      if (userType === 'organizer') {
        safeUserType = 'organizer';
        safeRoles = ['user', 'organizer'];
        // Validate required organizer fields
        const requiredFields = ['name', 'phoneNumber', 'website', 'description', 'businessType', 'address'];
        for (const field of requiredFields) {
          if (!additionalData || !additionalData[field]) {
            throw new Error(`Missing required organizer field: ${field}`);
          }
        }
        userDocData = {
          organizationName: additionalData.name,
          phoneNumber: additionalData.phoneNumber,
          website: additionalData.website,
          description: additionalData.description,
          businessType: additionalData.businessType,
          address: additionalData.address,
          photoURL: additionalData.photoURL || '',
        };
      } else {
        // Validate required user fields
        const requiredFields = ['name', 'phoneNumber', 'gender', 'address'];
        for (const field of requiredFields) {
          if (!additionalData || !additionalData[field]) {
            throw new Error(`Missing required user field: ${field}`);
          }
        }
        userDocData = {
          displayName: additionalData.name,
          phoneNumber: additionalData.phoneNumber,
          gender: additionalData.gender,
          address: additionalData.address,
        };
      }
      // Never allow 'admin' to be set via frontend
      if (userType === 'admin' || (additionalData && additionalData.roles && additionalData.roles.includes('admin'))) {
        throw new Error('Cannot assign admin role');
      }

      const result = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password
      );

      if (result.user) {
        await updateProfile(result.user, {
          displayName: name,
        });

        const newUser = {
          ...this.createNewUserObject(result.user, safeUserType, {
            ...userDocData,
            roles: safeRoles,
            userType: safeUserType,
            displayName: name,
          }),
        };
        await setDoc(doc(this.db, 'users', result.user.uid), newUser);
        return newUser;
      }

      throw new Error("User creation failed unexpectedly.");
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (!firebaseUser) {
        callback(null);
        return;
      }

      try {
        const userDoc = await getDoc(doc(this.db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as AuthUser;
          // Make sure roles exists
          if (!userData.roles) {
            userData.roles = ['user', 'organizer'];
          } else if (!userData.roles.includes('organizer')) {
            userData.roles.push('organizer');
          }
          
          // Update the user document with roles
          await updateDoc(doc(this.db, 'users', firebaseUser.uid), {
            roles: userData.roles,
            updatedAt: Timestamp.now()
          });
          
          callback(userData);
        } else {
          // This case is tricky. User exists in Firebase Auth, but not in Firestore.
          // This can happen if Firestore doc creation fails after Auth user creation.
          // Or if user was created via different means.
          // We'll create a new user doc here to be safe and ensure app consistency.
          console.warn(`User document for UID ${firebaseUser.uid} not found. Creating one.`);
          
          const isAdmin = firebaseUser.email === 'admin@example.com';
          const newUser: AuthUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL || undefined,
            emailVerified: firebaseUser.emailVerified,
            userType: isAdmin ? 'admin' : 'user',
            roles: isAdmin ? ['user', 'admin'] : ['user'],
            lastLoginAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            preferences: { notifications: true, emailMarketing: false, theme: 'light' },
            stats: {
              eventsCreated: 0,
              eventsAttended: 0,
              ticketsPurchased: 0,
              totalRevenue: 0,
              activeEvents: 0,
              totalBookings: 0
            }
          };
          
          await setDoc(doc(this.db, 'users', firebaseUser.uid), newUser);
          callback(newUser);
        }
      } catch (error) {
        console.error('Error getting user data:', error);
        callback(null);
      }
    });
  }

  public async getUserData(userId: string): Promise<AuthUser | null> {
    const userRef = doc(this.db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data() as AuthUser;
      const firebaseUser = this.auth.currentUser;
      const currentRoles = userData.roles || [];

      // Self-healing check for the admin user. This is the best place for it.
      if (firebaseUser && firebaseUser.email === 'admin@example.com' && !currentRoles.includes('admin')) {
        console.warn('Admin user detected with missing admin role. Correcting...');
        const updatedRoles = [...currentRoles, 'admin'];
        await updateDoc(userRef, {
          roles: updatedRoles,
          userType: 'admin'
        });
        userData.roles = updatedRoles;
        userData.userType = 'admin';
      }
      return userData;
    }
    return null;
  }

  public hasRole(user: AuthUser | null, role: string): boolean {
    if (!user) {
      return false;
    }
    
    // Make sure roles exists
    if (!user.roles) {
      return role === 'user' || role === 'organizer';
    }
    
    switch (role) {
      case 'user':
        return true;
      case 'organizer':
        return true;
      case 'admin':
        return user.roles.includes('admin');
      default:
        return user.roles.includes(role);
    }
  }

  public async updateUserPreferences(userId: string, preferences: Partial<AuthUser['preferences']>): Promise<void> {
    try {
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const currentPrefs = userDoc.data().preferences || {};
        // Only update if preferences actually changed
        if (JSON.stringify(currentPrefs) !== JSON.stringify(preferences)) {
          await updateDoc(userRef, {
            preferences: preferences,
            updatedAt: Timestamp.now()
          });
        }
      }
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public async updateUserStats(userId: string, stats: Partial<AuthUser['stats']>): Promise<void> {
    try {
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const currentStats = userDoc.data().stats || {};
        // Only update if stats actually changed
        if (JSON.stringify(currentStats) !== JSON.stringify(stats)) {
          await updateDoc(userRef, {
            stats: stats,
            updatedAt: Timestamp.now()
          });
        }
      }
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public async ensureUserRoles(userId: string): Promise<void> {
    try {
      const userDoc = await getDoc(doc(this.db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data() as AuthUser;
        const roles = userData.roles || [];
        // Ensure user has at least the basic roles
        let changed = false;
        if (!roles.includes('user')) {
          roles.push('user');
          changed = true;
        }
        if (!roles.includes('organizer')) {
          roles.push('organizer');
          changed = true;
        }
        // Only update if roles actually changed
        if (changed) {
          await updateDoc(doc(this.db, 'users', userId), {
            roles: roles,
            updatedAt: Timestamp.now()
          });
        }
      } else {
        // If user doesn't exist in Firestore but exists in Firebase Auth
        const firebaseUser = this.auth.currentUser;
        if (firebaseUser && firebaseUser.uid === userId) {
          // Create a new user document with default roles
          const newUser: AuthUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL || undefined,
            emailVerified: firebaseUser.emailVerified,
            userType: 'user',
            roles: ['user', 'organizer'],
            lastLoginAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            preferences: {
              notifications: true,
              emailMarketing: false,
              theme: 'light'
            },
            stats: {
              eventsCreated: 0,
              eventsAttended: 0,
              ticketsPurchased: 0,
              totalRevenue: 0,
              activeEvents: 0,
              totalBookings: 0
            }
          };
          await setDoc(doc(this.db, 'users', firebaseUser.uid), newUser);
        }
      }
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  public async handleMissingUser(firebaseUser: FirebaseUser): Promise<AuthUser> {
    console.warn(`User document for UID ${firebaseUser.uid} not found in Firestore. Creating one.`);
    const newUser = this.createNewUserObject(firebaseUser);
    await setDoc(doc(this.db, 'users', firebaseUser.uid), newUser);
    return newUser;
  }

  private createNewUserObject(
    firebaseUser: FirebaseUser,
    userType: UserType = 'user',
    additionalData: Partial<AuthUser> = {}
  ): AuthUser {
    const roles: Role[] =
      userType === 'organizer' ? ['user', 'organizer'] : ['user'];

    const baseUser: AuthUser = {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || additionalData.displayName || 'New User',
      photoURL: firebaseUser.photoURL,
      emailVerified: firebaseUser.emailVerified,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      lastLoginAt: serverTimestamp() as Timestamp,
      roles,
      userType,
      preferences: {
        notifications: true,
        emailMarketing: false,
        theme: 'light',
      },
      stats: {
        eventsCreated: 0,
        eventsAttended: 0,
        ticketsPurchased: 0,
        totalRevenue: 0,
        activeEvents: 0,
        totalBookings: 0,
      },
    };

    if (userType === 'organizer') {
      return {
        ...baseUser,
        isOrganizer: true,
        ...additionalData,
      };
    }

    return {
      ...baseUser,
      ...additionalData,
    };
  }
}

export const authService = AuthService.getInstance(); 